import {
  BadgeInfo,
  BookmarkX,
  BrainCircuit,
  Captions,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CirclePlay,
  Clapperboard,
  Clock3,
  CloudDownload,
  Copy,
  ExternalLink,
  Eye,
  Gauge,
  Globe2,
  HardDrive,
  HardDriveDownload,
  KeyRound,
  Languages,
  LockKeyhole,
  type LucideIcon,
  MoonStar,
  Network,
  Pencil,
  Plus,
  RefreshCw,
  Rss,
  Save,
  Search,
  Settings2,
  Sparkles,
  SunMoon,
  TableProperties,
  Trash2,
  UserPlus,
  UserRound,
  Users,
} from 'lucide-react';
import {
  type CSSProperties,
  type FormEvent,
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import packageInfo from '../../../package.json' with { type: 'json' };

import logoUrl from '../assets/logo.png';
import { useApp } from '../components/app-context';
import { Alert } from '../components/alert';
import { Button, ButtonLink } from '../components/button';
import { Dialog } from '../components/dialog';
import { Divider } from '../components/divider';
import { HomeLayoutEditor } from '../components/home-layout-editor';
import { Input } from '../components/input';
import { Select } from '../components/select';
import { SettingsCard } from '../components/settings-card';
import { Switch } from '../components/switch';
import { t } from '../i18n';
import {
  $aiSelectionPrompt,
  $autoMarkWatched,
  $autoplay,
  $autoPlayNextEpisode,
  $currentUser,
  $japaneseAnimeOnly,
  $showLateNightAnime,
  $showNsfw,
  $subtitleLanguage,
  $subtitlesEnabled,
  $themeMode,
  $useAiMediaSelection,
  setAiSelectionPrompt,
  setAutoMarkWatched,
  setAutoplay,
  setAutoPlayNextEpisode,
  setJapaneseAnimeOnly,
  setShowLateNightAnime,
  setShowNsfw,
  setSubtitleLanguage,
  setSubtitlesEnabled,
  setThemeMode,
  setUseAiMediaSelection,
  type SubtitleLanguage,
  type ThemeMode,
} from '../store/app.store';
import { useStore } from '../hooks/use-store';
import { useSettings } from '../hooks/use-settings';
import type { BgmCurrentUser } from '../types/bgm';
import type {
  AiAdminSettings,
  AiPublicSettings,
  ClearWatchLaterResult,
  ClearWatchProgressResult,
  CurrentUser,
  DownloadManagementTask,
  MediaProviderInfo,
  MediaProviderTemplateContext,
  SettingsSection,
  StorageInfo,
  UpdateInfo,
  UserInfo,
} from '../types/page';
import { formatDateTime } from '../utils/date';
import { safeUrl } from '../utils/url';

interface SettingsMenuItem {
  icon: LucideIcon;
  label: string;
  value: SettingsSection;
}

type SettingsCardId =
  | 'aiConnection'
  | 'autoMarkWatched'
  | 'autoPlayNextEpisode'
  | 'autoplay'
  | 'bgmToken'
  | 'changePassword'
  | 'checkForUpdates'
  | 'clearDownloads'
  | 'clearWatchLater'
  | 'clearWatchHistory'
  | 'customizeHome'
  | 'downloadRetention'
  | 'downloadTasks'
  | 'japaneseAnimeOnly'
  | 'mediaResourceSelection'
  | 'rssMediaProviders'
  | 'showLateNightAnime'
  | 'showNsfw'
  | 'storageSpace'
  | 'subtitleSettings'
  | 'toggleLanguage'
  | 'toggleTheme'
  | 'transferRateLimits'
  | 'useAdditionalTrackers'
  | 'users';

interface SettingsSearchItem {
  id: SettingsCardId;
  section: SettingsSection;
  title: string;
}

interface FilteredSettingsProps {
  visibleCards?: ReadonlySet<SettingsCardId>;
}

const isSettingsCardVisible = (visibleCards: ReadonlySet<SettingsCardId> | undefined, id: SettingsCardId): boolean =>
  !visibleCards || visibleCards.has(id);

const settingsFieldClassName =
  'grid min-w-0 gap-2 [&>span]:m-0 [&>span]:text-[11px] [&>span]:font-black [&>span]:text-(--muted)';

export const SettingsPage = ({ section }: { section?: SettingsSection }) => {
  const { locale, navigate } = useApp();
  const user = useStore($currentUser);
  const [searchQuery, setSearchQuery] = useState('');
  if (!user) {
    return null;
  }
  const items: SettingsMenuItem[] = [
    {
      value: 'appearance',
      label: t('applicationAppearance'),
      icon: Settings2,
    },
    {
      value: 'playback',
      label: t('playbackPage'),
      icon: Clapperboard,
    },
    { value: 'ai', label: t('aiIntegration'), icon: Sparkles },
    {
      value: 'account',
      label: t('account'),
      icon: UserRound,
    },
    ...(user.role === 'admin'
      ? [
          {
            value: 'subscriptions' as const,
            label: t('subscriptions'),
            icon: Rss,
          },
          {
            value: 'downloads' as const,
            label: t('downloads'),
            icon: HardDriveDownload,
          },
          {
            value: 'users' as const,
            label: t('users'),
            icon: Users,
          },
        ]
      : []),
    {
      value: 'about',
      label: t('about'),
      icon: BadgeInfo,
    },
  ];
  const activeItem = items.find((item) => item.value === section);
  const searchItems: SettingsSearchItem[] = [
    { id: 'toggleTheme', section: 'appearance', title: t('toggleTheme') },
    { id: 'toggleLanguage', section: 'appearance', title: t('toggleLanguage') },
    { id: 'showNsfw', section: 'appearance', title: t('showNsfw') },
    { id: 'japaneseAnimeOnly', section: 'appearance', title: t('japaneseAnimeOnly') },
    { id: 'showLateNightAnime', section: 'appearance', title: t('showLateNightAnime') },
    { id: 'customizeHome', section: 'appearance', title: t('customizeHome') },
    { id: 'autoplay', section: 'playback', title: t('autoplay') },
    { id: 'autoMarkWatched', section: 'playback', title: t('autoMarkWatched') },
    { id: 'autoPlayNextEpisode', section: 'playback', title: t('autoPlayNextEpisode') },
    { id: 'subtitleSettings', section: 'playback', title: t('subtitleSettings') },
    { id: 'mediaResourceSelection', section: 'ai', title: t('mediaResourceSelection') },
    ...(user.role === 'admin'
      ? [{ id: 'aiConnection' as const, section: 'ai' as const, title: t('aiConnection') }]
      : []),
    { id: 'bgmToken', section: 'account', title: 'Bangumi Token' },
    { id: 'changePassword', section: 'account', title: t('changePassword') },
    { id: 'clearWatchHistory', section: 'account', title: t('clearWatchHistory') },
    { id: 'clearWatchLater', section: 'account', title: t('clearWatchLater') },
    { id: 'checkForUpdates', section: 'about', title: t('checkForUpdates') },
    ...(user.role === 'admin'
      ? [
          { id: 'rssMediaProviders' as const, section: 'subscriptions' as const, title: t('rssMediaProviders') },
          { id: 'downloadTasks' as const, section: 'downloads' as const, title: t('downloadTasks') },
          { id: 'storageSpace' as const, section: 'downloads' as const, title: t('storageSpace') },
          { id: 'downloadRetention' as const, section: 'downloads' as const, title: t('downloadRetention') },
          { id: 'transferRateLimits' as const, section: 'downloads' as const, title: t('transferRateLimits') },
          {
            id: 'useAdditionalTrackers' as const,
            section: 'downloads' as const,
            title: t('useAdditionalTrackers'),
          },
          { id: 'clearDownloads' as const, section: 'downloads' as const, title: t('clearDownloads') },
          { id: 'users' as const, section: 'users' as const, title: t('users') },
        ]
      : []),
  ];
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase(locale);
  const matchedCardsBySection = searchItems.reduce((result, item) => {
    if (!normalizedSearchQuery || !item.title.toLocaleLowerCase(locale).includes(normalizedSearchQuery)) {
      return result;
    }
    const matchedCards = result.get(item.section) ?? new Set<SettingsCardId>();
    matchedCards.add(item.id);
    result.set(item.section, matchedCards);
    return result;
  }, new Map<SettingsSection, Set<SettingsCardId>>());

  const renderSection = (value: SettingsSection | undefined, visibleCards?: ReadonlySet<SettingsCardId>): ReactNode => {
    switch (value) {
      case 'about':
        return <AboutSettings visibleCards={visibleCards} />;
      case 'account':
        return <AccountSettings user={user} visibleCards={visibleCards} />;
      case 'appearance':
        return <AppearanceSettings visibleCards={visibleCards} />;
      case 'playback':
        return <PlaybackSettings visibleCards={visibleCards} />;
      case 'ai':
        return <AiSettings user={user} visibleCards={visibleCards} />;
      case 'downloads':
        return user.role === 'admin' ? <DownloadManagement visibleCards={visibleCards} /> : null;
      case 'subscriptions':
        return user.role === 'admin' ? <MediaProviderManagement visibleCards={visibleCards} /> : null;
      case 'users':
        return user.role === 'admin' ? <UserManagement currentUser={user} visibleCards={visibleCards} /> : null;
      default:
        return null;
    }
  };
  const content = renderSection(activeItem?.value);

  return (
    <section>
      {activeItem ? (
        <Button
          variant="plain"
          className="mb-5 inline-flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-xs font-bold text-(--muted) transition-colors duration-180 hover:text-(--input-accent) focus-visible:text-(--input-accent) focus-visible:outline-0"
          onClick={() => navigate('/settings')}
        >
          <ChevronLeft className="size-4" />
          <span>{t('settings')}</span>
        </Button>
      ) : null}
      <div className="hero-copy compact-hero mb-6">
        {!activeItem ? <p className="eyebrow">SETTINGS</p> : null}
        <h1>{activeItem?.label ?? t('settings')}</h1>
      </div>
      {activeItem ? (
        content
      ) : (
        <>
          <Input
            wrapperClassName="mb-5"
            icon={<Search className="size-4 text-(--muted)" />}
            value={searchQuery}
            placeholder={t('searchSettings')}
            aria-label={t('searchSettings')}
            clearable
            clearLabel={t('clearInput')}
            onChange={(event) => setSearchQuery(event.currentTarget.value)}
            onClear={() => setSearchQuery('')}
          />
          {normalizedSearchQuery ? (
            matchedCardsBySection.size > 0 ? (
              <div className="grid gap-4">
                {[...matchedCardsBySection].map(([matchedSection, visibleCards]) => (
                  <Fragment key={matchedSection}>{renderSection(matchedSection, visibleCards)}</Fragment>
                ))}
              </div>
            ) : (
              <div className="simple-card py-10 text-center text-xs text-(--muted)">{t('noSettingsFound')}</div>
            )
          ) : (
            <nav className="grid gap-3" aria-label={t('settings')}>
              {items.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <Button
                    variant="plain"
                    className="group grid min-w-0 cursor-pointer grid-cols-[42px_minmax(0,1fr)_18px] items-center gap-3.5 rounded-2xl border border-(--line) bg-(--surface) p-4 text-left text-inherit transition-[border-color,background,transform] duration-180 hover:-translate-y-px hover:border-[color-mix(in_srgb,var(--input-accent)_38%,var(--line))] hover:bg-[color-mix(in_srgb,var(--input-accent)_5%,var(--surface))] focus-visible:-translate-y-px focus-visible:border-[color-mix(in_srgb,var(--input-accent)_38%,var(--line))] focus-visible:bg-[color-mix(in_srgb,var(--input-accent)_5%,var(--surface))] focus-visible:outline-0"
                    key={item.value}
                    onClick={() => navigate(`/settings/${item.value}`)}
                  >
                    <span className="grid size-10.5 place-items-center rounded-xl bg-[color-mix(in_srgb,var(--input-accent)_10%,transparent)] text-(--input-accent)">
                      <ItemIcon className="size-5" />
                    </span>
                    <span className="min-w-0">
                      <strong className="text-[13px]">{item.label}</strong>
                    </span>
                    <ChevronRight className="size-4 text-(--muted) transition-transform duration-180 group-hover:translate-x-0.5 group-hover:text-(--input-accent) group-focus-visible:translate-x-0.5 group-focus-visible:text-(--input-accent)" />
                  </Button>
                );
              })}
            </nav>
          )}
        </>
      )}
    </section>
  );
};

const AboutSettings = ({ visibleCards }: FilteredSettingsProps) => {
  const { api, locale } = useApp();
  const repositoryUrl = packageInfo.repository.url.replace(/\.git$/, '');
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo>();
  const [updateError, setUpdateError] = useState('');
  const [checking, setChecking] = useState(true);
  const [checkRevision, setCheckRevision] = useState(0);

  useEffect(() => {
    if (!isSettingsCardVisible(visibleCards, 'checkForUpdates')) {
      return;
    }
    let active = true;
    setChecking(true);
    setUpdateError('');
    void api<UpdateInfo>('/api/update')
      .then((result) => {
        if (active) {
          setUpdateInfo(result);
        }
      })
      .catch((error) => {
        if (active) {
          setUpdateInfo(undefined);
          setUpdateError(error instanceof Error ? error.message : String(error));
        }
      })
      .finally(() => {
        if (active) {
          setChecking(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api, checkRevision, visibleCards]);

  const statusText = updateInfo
    ? updateInfo.status === 'available'
      ? t('updateAvailable').replace('{version}', `v${updateInfo.latestVersion}`)
      : updateInfo.status === 'ahead'
        ? t('updateVersionAhead')
        : t('updateUpToDate')
    : '';

  return (
    <div className="settings-panel grid gap-4">
      {!visibleCards ? (
        <article className="simple-card grid gap-7">
          <div className="flex flex-col items-center gap-3 text-center">
            <img className="size-18 shrink-0 rounded-2xl" src={logoUrl} alt="BGMer" />
            <h2 className="m-0 text-2xl">BGMer</h2>
          </div>
          <dl className="grid gap-3 border-t border-(--line) pt-5 [&>div]:flex [&>div]:items-center [&>div]:justify-between [&>div]:gap-5 [&>div]:py-1 [&>div+div]:border-t [&>div+div]:border-(--line) [&>div+div]:pt-4 [&_dd]:m-0 [&_dd]:text-xs [&_dd]:font-bold [&_dt]:text-[10px] [&_dt]:text-(--muted)">
            <div>
              <dt>{t('version')}</dt>
              <dd>v{packageInfo.version}</dd>
            </div>
            <div>
              <dt>{t('author')}</dt>
              <dd>{packageInfo.author}</dd>
            </div>
            <div>
              <dt>{t('openSourceLicense')}</dt>
              <dd>{packageInfo.license}</dd>
            </div>
            <div>
              <dt>GitHub</dt>
              <dd>
                <a
                  className="inline-flex items-center gap-1.5 text-inherit no-underline hover:text-(--input-accent) focus-visible:text-(--input-accent)"
                  href={repositoryUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <span>nepsyn/bgmer</span>
                  <ExternalLink className="size-3 text-(--muted)" />
                </a>
              </dd>
            </div>
          </dl>
        </article>
      ) : null}
      {isSettingsCardVisible(visibleCards, 'checkForUpdates') ? (
        <SettingsCard icon={RefreshCw} title={t('checkForUpdates')}>
          {checking ? (
            <div className="flex min-h-16 items-center justify-center gap-2 text-xs text-(--muted)">
              <RefreshCw className="size-4 spin" />
              <span>{t('checkingForUpdates')}</span>
            </div>
          ) : updateError ? (
            <div className="grid gap-3">
              <Alert variant="error">{updateError}</Alert>
              <div>
                <Button compact onClick={() => setCheckRevision((current) => current + 1)}>
                  <RefreshCw className="size-3.5!" />
                  {t('refresh')}
                </Button>
              </div>
            </div>
          ) : updateInfo ? (
            <div className="flex items-center justify-between gap-4 max-[700px]:flex-col max-[700px]:items-stretch">
              <div className="grid min-w-0 gap-1">
                <strong className="text-xs">{statusText}</strong>
                <small className="text-[10px] text-(--muted)">
                  {t('latestVersion')}: v{updateInfo.latestVersion} ·{' '}
                  {t('checkedAt').replace('{time}', formatDateTime(updateInfo.checkedAt, locale))}
                </small>
              </div>
              <div className="flex shrink-0 items-center gap-2 max-[700px]:w-full max-[700px]:[&>*]:flex-1">
                <Button
                  compact
                  aria-label={t('checkForUpdates')}
                  title={t('checkForUpdates')}
                  onClick={() => setCheckRevision((current) => current + 1)}
                >
                  <RefreshCw className="size-3.5!" />
                  {t('refresh')}
                </Button>
                {updateInfo.status === 'available' ? (
                  <ButtonLink compact href={updateInfo.releaseUrl} target="_blank" rel="noreferrer">
                    <ExternalLink className="size-3.5!" />
                    {t('viewUpdate')}
                  </ButtonLink>
                ) : null}
              </div>
            </div>
          ) : null}
        </SettingsCard>
      ) : null}
    </div>
  );
};

const AccountSettings = ({ user, visibleCards }: { user: CurrentUser } & FilteredSettingsProps) => {
  const { api, bgmAuth, showToast } = useApp();
  const [token, setToken] = useState('');
  const [testing, setTesting] = useState(false);
  const [account, setAccount] = useState<BgmCurrentUser>();
  const [clearWatchHistoryDialogOpen, setClearWatchHistoryDialogOpen] = useState(false);
  const [clearingWatchHistory, setClearingWatchHistory] = useState(false);
  const [clearWatchLaterDialogOpen, setClearWatchLaterDialogOpen] = useState(false);
  const [clearingWatchLater, setClearingWatchLater] = useState(false);

  const loadAccount = useCallback(async () => {
    if (!user.bgmToken) {
      setAccount(undefined);
      return;
    }
    try {
      setAccount(await bgmAuth<BgmCurrentUser>('/v0/me'));
    } catch {
      setAccount(undefined);
    }
  }, [bgmAuth, user.bgmToken]);
  useEffect(() => void loadAccount(), [loadAccount]);

  const saveToken = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    try {
      const updated = await api<CurrentUser>('/api/user', {
        method: 'PATCH',
        body: JSON.stringify({ bgmToken: token.trim() || null }),
      });
      $currentUser.set(updated);
      setToken('');
      showToast(t('tokenUpdated'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    }
  };
  const testToken = async (): Promise<void> => {
    if (!token.trim()) {
      return;
    }
    setTesting(true);
    try {
      const result = await api<{ valid: boolean; username?: string }>('/api/user/bgm-token/test', {
        method: 'POST',
        body: JSON.stringify({ bgmToken: token.trim() }),
      });
      showToast(
        result.valid ? `${t('tokenValid')}${result.username ? `: ${result.username}` : ''}` : t('tokenInvalid'),
        result.valid ? 'success' : 'error',
      );
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setTesting(false);
    }
  };
  const updatePassword = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      await api('/api/user', {
        method: 'PATCH',
        body: JSON.stringify({ currentPassword: data.get('currentPassword'), newPassword: data.get('newPassword') }),
      });
      form.reset();
      showToast(t('passwordUpdated'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    }
  };
  const clearWatchHistory = async (): Promise<void> => {
    setClearingWatchHistory(true);
    try {
      await api<ClearWatchProgressResult>('/api/watch-progress', { method: 'DELETE' });
      setClearWatchHistoryDialogOpen(false);
      showToast(t('watchHistoryCleared'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setClearingWatchHistory(false);
    }
  };
  const clearWatchLater = async (): Promise<void> => {
    setClearingWatchLater(true);
    try {
      await api<ClearWatchLaterResult>('/api/watch-later', { method: 'DELETE' });
      setClearWatchLaterDialogOpen(false);
      showToast(t('watchLaterCleared'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setClearingWatchLater(false);
    }
  };
  const avatar = safeUrl(account?.avatar?.medium ?? account?.avatar?.large ?? account?.avatar?.small);
  return (
    <div className="settings-panel grid gap-4">
      {isSettingsCardVisible(visibleCards, 'bgmToken') ? (
        <SettingsCard
          icon={KeyRound}
          title="Bangumi Token"
          description={t('bgmTokenDescription')}
          headingClassName="token-card-heading"
          headingAction={
            <ButtonLink
              className="token-create-link"
              href="https://next.bgm.tv/demo/access-token"
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink />
              <span>{t('generateToken')}</span>
            </ButtonLink>
          }
        >
          {account && (
            <div className="bgm-account">
              <div className="bgm-account-avatar">
                {avatar ? <img src={avatar} alt="" referrerPolicy="no-referrer" /> : <UserRound />}
              </div>
              <div className="bgm-account-copy">
                <strong>{account.nickname || account.username}</strong>
                <span>
                  @{account.username} · ID {account.id}
                </span>
                {account.sign && <small>{account.sign}</small>}
              </div>
            </div>
          )}
          <form className="grid gap-4.5" onSubmit={(event) => void saveToken(event)}>
            <Input
              fieldClassName={settingsFieldClassName}
              label="Access Token"
              value={token}
              onChange={(event) => setToken(event.target.value)}
              type="password"
              placeholder={t('tokenPlaceholder')}
              autoComplete="off"
            />
            <div className="flex items-center justify-end gap-2.25">
              <Button className="min-h-11!" disabled={!token.trim() || testing} onClick={() => void testToken()}>
                {t('testToken')}
              </Button>
              <Button variant="primary" compact type="submit">
                <Save className="size-4!" />
                {t('saveSettings')}
              </Button>
            </div>
          </form>
        </SettingsCard>
      ) : null}
      {isSettingsCardVisible(visibleCards, 'changePassword') ? (
        <SettingsCard icon={LockKeyhole} title={t('changePassword')} description={t('passwordDescription')}>
          <form className="grid gap-4.5" onSubmit={(event) => void updatePassword(event)}>
            <Input
              fieldClassName={settingsFieldClassName}
              label={t('currentPassword')}
              name="currentPassword"
              type="password"
              required
              autoComplete="current-password"
            />
            <Input
              fieldClassName={settingsFieldClassName}
              label={t('newPassword')}
              name="newPassword"
              type="password"
              minLength={6}
              required
              autoComplete="new-password"
            />
            <Button variant="primary" compact type="submit">
              {t('updatePassword')}
            </Button>
          </form>
        </SettingsCard>
      ) : null}
      {isSettingsCardVisible(visibleCards, 'clearWatchHistory') ? (
        <SettingsCard
          className="settings-danger-card"
          icon={Trash2}
          title={t('clearWatchHistory')}
          description={t('clearWatchHistoryDescription')}
        >
          <Button color="danger" compact onClick={() => setClearWatchHistoryDialogOpen(true)}>
            <Trash2 className="size-4!" />
            <span>{t('clearWatchHistory')}</span>
          </Button>
        </SettingsCard>
      ) : null}
      {isSettingsCardVisible(visibleCards, 'clearWatchLater') ? (
        <SettingsCard
          className="settings-danger-card"
          icon={BookmarkX}
          title={t('clearWatchLater')}
          description={t('clearWatchLaterDescription')}
        >
          <Button color="danger" compact onClick={() => setClearWatchLaterDialogOpen(true)}>
            <BookmarkX className="size-4!" />
            <span>{t('clearWatchLater')}</span>
          </Button>
        </SettingsCard>
      ) : null}
      <Dialog
        open={clearWatchHistoryDialogOpen}
        title={t('confirmClearWatchHistory')}
        description={t('confirmClearWatchHistoryDescription')}
        confirmText={t('clearWatchHistory')}
        confirmColor="danger"
        loading={clearingWatchHistory}
        onCancel={() => setClearWatchHistoryDialogOpen(false)}
        onConfirm={() => void clearWatchHistory()}
      />
      <Dialog
        open={clearWatchLaterDialogOpen}
        title={t('confirmClearWatchLater')}
        description={t('confirmClearWatchLaterDescription')}
        confirmText={t('clearWatchLater')}
        confirmColor="danger"
        loading={clearingWatchLater}
        onCancel={() => setClearWatchLaterDialogOpen(false)}
        onConfirm={() => void clearWatchLater()}
      />
    </div>
  );
};

const AppearanceSettings = ({ visibleCards }: FilteredSettingsProps) => {
  const { changeLocale, locale } = useApp();
  const themeMode = useStore($themeMode);
  const showNsfw = useStore($showNsfw);
  const japaneseAnimeOnly = useStore($japaneseAnimeOnly);
  const showLateNightAnime = useStore($showLateNightAnime);
  const cards = [
    {
      id: 'toggleTheme' as const,
      icon: SunMoon,
      title: t('toggleTheme'),
      description: t('toggleThemeDescription'),
      action: (
        <Select
          className="w-38!"
          value={themeMode}
          ariaLabel={t('toggleTheme')}
          options={[
            { value: 'system', label: t('themeFollowSystem') },
            { value: 'light', label: t('themeLight') },
            { value: 'dark', label: t('themeDark') },
          ]}
          onChange={(value) => setThemeMode(value as ThemeMode)}
        />
      ),
    },
    {
      id: 'toggleLanguage' as const,
      icon: Languages,
      title: t('toggleLanguage'),
      description: t('toggleLanguageDescription'),
      action: (
        <Button variant="text" onClick={() => changeLocale(locale === 'zh-CN' ? 'en' : 'zh-CN')}>
          <Languages />
          <span>{t('toggleLanguage')}</span>
        </Button>
      ),
    },
    {
      id: 'showNsfw' as const,
      icon: Eye,
      title: t('showNsfw'),
      description: t('showNsfwDescription'),
      action: <Switch label={t('showNsfw')} checked={showNsfw} onChange={setShowNsfw} />,
    },
    {
      id: 'japaneseAnimeOnly' as const,
      icon: Globe2,
      title: t('japaneseAnimeOnly'),
      description: t('japaneseAnimeOnlyDescription'),
      action: <Switch label={t('japaneseAnimeOnly')} checked={japaneseAnimeOnly} onChange={setJapaneseAnimeOnly} />,
    },
    {
      id: 'showLateNightAnime' as const,
      icon: MoonStar,
      title: t('showLateNightAnime'),
      description: t('showLateNightAnimeDescription'),
      action: <Switch label={t('showLateNightAnime')} checked={showLateNightAnime} onChange={setShowLateNightAnime} />,
    },
  ];
  return (
    <div className="settings-panel grid gap-4">
      {cards
        .filter((card) => isSettingsCardVisible(visibleCards, card.id))
        .map((card) => {
          return (
            <SettingsCard
              className="appearance-card"
              icon={card.icon}
              title={card.title}
              description={card.description}
              key={card.title}
            >
              {card.action}
            </SettingsCard>
          );
        })}
      {isSettingsCardVisible(visibleCards, 'customizeHome') ? <HomeLayoutEditor /> : null}
    </div>
  );
};

const PlaybackSettings = ({ visibleCards }: FilteredSettingsProps) => {
  const autoplay = useStore($autoplay);
  const autoPlayNextEpisode = useStore($autoPlayNextEpisode);
  const autoMarkWatched = useStore($autoMarkWatched);
  const subtitlesEnabled = useStore($subtitlesEnabled);
  const subtitleLanguage = useStore($subtitleLanguage);
  const cards = [
    {
      id: 'autoplay' as const,
      icon: CirclePlay,
      title: t('autoplay'),
      description: t('autoplayDescription'),
      checked: autoplay,
      onChange: setAutoplay,
    },
    {
      id: 'autoMarkWatched' as const,
      icon: CheckCircle2,
      title: t('autoMarkWatched'),
      description: t('autoMarkWatchedDescription'),
      checked: autoMarkWatched,
      onChange: setAutoMarkWatched,
    },
    {
      id: 'autoPlayNextEpisode' as const,
      icon: Clapperboard,
      title: t('autoPlayNextEpisode'),
      description: t('autoPlayNextEpisodeDescription'),
      checked: autoPlayNextEpisode,
      onChange: setAutoPlayNextEpisode,
    },
  ];

  return (
    <div className="settings-panel grid gap-4">
      {cards
        .filter((card) => isSettingsCardVisible(visibleCards, card.id))
        .map((card) => {
          return (
            <SettingsCard
              className="appearance-card"
              icon={card.icon}
              title={card.title}
              description={card.description}
              key={card.title}
            >
              <Switch label={card.title} checked={card.checked} onChange={card.onChange} />
            </SettingsCard>
          );
        })}
      {isSettingsCardVisible(visibleCards, 'subtitleSettings') ? (
        <SettingsCard
          className="subtitle-settings-card"
          icon={Captions}
          title={t('subtitleSettings')}
          description={t('subtitleSettingsDescription')}
        >
          <div className="subtitle-setting-row flex min-h-14 items-center justify-between gap-6 py-4 max-[700px]:gap-3">
            <div>
              <strong>{t('showSubtitlesByDefault')}</strong>
              <p>{t('showSubtitlesByDefaultDescription')}</p>
            </div>
            <Switch label={t('showSubtitlesByDefault')} checked={subtitlesEnabled} onChange={setSubtitlesEnabled} />
          </div>
          <div className="subtitle-setting-row flex min-h-14 items-center justify-between gap-6 py-4 max-[700px]:gap-3">
            <div>
              <strong>{t('defaultSubtitleLanguage')}</strong>
              <p>{t('defaultSubtitleLanguageDescription')}</p>
            </div>
            <Select
              className="w-52! shrink-0 max-[700px]:w-36!"
              ariaLabel={t('defaultSubtitleLanguage')}
              value={subtitleLanguage}
              disabled={!subtitlesEnabled}
              options={[
                { value: 'auto', label: t('subtitleLanguageAuto') },
                { value: 'zh-CN', label: t('subtitleLanguageSimplifiedChinese') },
                { value: 'zh-TW', label: t('subtitleLanguageTraditionalChinese') },
                { value: 'ja', label: t('subtitleLanguageJapanese') },
                { value: 'en', label: t('subtitleLanguageEnglish') },
              ]}
              onChange={(value) => setSubtitleLanguage(value as SubtitleLanguage)}
            />
          </div>
        </SettingsCard>
      ) : null}
    </div>
  );
};

const AiSettings = ({ user, visibleCards }: { user: CurrentUser } & FilteredSettingsProps) => {
  const { api, showToast } = useApp();
  const useAiMediaSelection = useStore($useAiMediaSelection);
  const prompt = useStore($aiSelectionPrompt);
  const [settings, setSettings] = useState<AiPublicSettings | AiAdminSettings>();
  const [enabled, setEnabled] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [updatingEnabled, setUpdatingEnabled] = useState(false);
  const [testing, setTesting] = useState(false);
  const [clearingApiKey, setClearingApiKey] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (user.role === 'admin') {
      const next = await api<AiAdminSettings>('/api/admin/ai-settings');
      setSettings(next);
      setEnabled(next.enabled);
      setApiUrl(next.apiUrl ?? '');
      setModel(next.model ?? '');
      return;
    }
    setSettings(await api<AiPublicSettings>('/api/ai-settings'));
  }, [api, user.role]);

  useEffect(() => {
    void load().catch((reason) => showToast(reason instanceof Error ? reason.message : String(reason), 'error'));
  }, [load, showToast]);

  const updateEnabled = async (nextEnabled: boolean): Promise<void> => {
    const previousEnabled = enabled;
    setEnabled(nextEnabled);
    setUpdatingEnabled(true);
    try {
      const next = await api<AiAdminSettings>('/api/admin/ai-settings', {
        method: 'PATCH',
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      setSettings(next);
      setEnabled(next.enabled);
    } catch (reason) {
      setEnabled(previousEnabled);
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setUpdatingEnabled(false);
    }
  };

  const saveConnection = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    try {
      const next = await api<AiAdminSettings>('/api/admin/ai-settings', {
        method: 'PATCH',
        body: JSON.stringify({
          enabled,
          apiUrl: apiUrl.trim() || null,
          model: model.trim() || null,
          ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        }),
      });
      setSettings(next);
      setEnabled(next.enabled);
      setApiKey('');
      showToast(t('aiSettingsSaved'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (): Promise<void> => {
    setTesting(true);
    try {
      await api('/api/admin/ai-settings/test', {
        method: 'POST',
        body: JSON.stringify({
          apiUrl: apiUrl.trim() || null,
          model: model.trim() || null,
          ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        }),
      });
      showToast(t('aiConnectionSucceeded'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setTesting(false);
    }
  };

  const clearApiKey = async (): Promise<void> => {
    setClearingApiKey(true);
    try {
      const next = await api<AiAdminSettings>('/api/admin/ai-settings', {
        method: 'PATCH',
        body: JSON.stringify({ apiKey: null }),
      });
      setSettings(next);
      setApiKey('');
      showToast(t('aiSettingsSaved'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setClearingApiKey(false);
    }
  };

  const adminSettings = user.role === 'admin' ? (settings as AiAdminSettings | undefined) : undefined;

  return (
    <section className="settings-panel grid gap-4">
      {isSettingsCardVisible(visibleCards, 'mediaResourceSelection') ? (
        <SettingsCard
          className="ai-media-selection-card"
          icon={Sparkles}
          title={t('mediaResourceSelection')}
          headingDetails={
            <small className={`ai-service-status ${settings?.available ? 'available' : ''}`.trim()}>
              {t(settings?.available ? 'aiServiceAvailable' : 'aiServiceUnavailable')}
            </small>
          }
        >
          <div className="subtitle-setting-row flex min-h-14 items-center justify-between gap-6 py-4 max-[700px]:gap-3">
            <div>
              <strong>{t('useAiMediaSelection')}</strong>
              <p>{t('useAiMediaSelectionDescription')}</p>
            </div>
            <Switch label={t('useAiMediaSelection')} checked={useAiMediaSelection} onChange={setUseAiMediaSelection} />
          </div>
          <div className="ai-preference-grid mt-5 grid gap-4">
            <label className={settingsFieldClassName}>
              <span>{t('aiSelectionPrompt')}</span>
              <textarea
                maxLength={2000}
                placeholder={t('aiSelectionPromptPlaceholder')}
                value={prompt}
                onChange={(event) => setAiSelectionPrompt(event.currentTarget.value)}
              />
            </label>
          </div>
        </SettingsCard>
      ) : null}

      {user.role === 'admin' && isSettingsCardVisible(visibleCards, 'aiConnection') ? (
        <SettingsCard icon={BrainCircuit} title={t('aiConnection')} description={t('aiConnectionDescription')}>
          <form className="mt-5 grid gap-4" onSubmit={(event) => void saveConnection(event)}>
            <div className="subtitle-setting-row flex min-h-14 items-center justify-between gap-6 py-0 pb-4 pl-0 max-[700px]:gap-3">
              <div>
                <strong>{t('aiEnabled')}</strong>
                <p>{t(enabled ? 'aiServiceAvailable' : 'aiServiceUnavailable')}</p>
              </div>
              <Switch
                label={t('aiEnabled')}
                checked={enabled}
                disabled={updatingEnabled || saving}
                onChange={(checked) => void updateEnabled(checked)}
              />
            </div>
            <Input
              fieldClassName={settingsFieldClassName}
              label={t('aiApiUrl')}
              type="url"
              placeholder={t('aiApiUrlPlaceholder')}
              value={apiUrl}
              onChange={(event) => setApiUrl(event.currentTarget.value)}
            />
            <div className="grid gap-2">
              <Input
                fieldClassName={settingsFieldClassName}
                label={t('aiApiKey')}
                type="password"
                autoComplete="new-password"
                placeholder={adminSettings?.apiKeyMasked ?? t('aiApiKeyPlaceholder')}
                value={apiKey}
                onChange={(event) => setApiKey(event.currentTarget.value)}
              />
              {adminSettings?.apiKeyConfigured && !adminSettings.apiKeyAvailable ? (
                <Alert variant="error">{t('aiApiKeyUnavailable')}</Alert>
              ) : null}
            </div>
            <Input
              fieldClassName={settingsFieldClassName}
              label={t('aiModel')}
              placeholder={t('aiModelPlaceholder')}
              value={model}
              onChange={(event) => setModel(event.currentTarget.value)}
            />
            <div className="flex justify-end gap-2 max-[700px]:grid max-[700px]:grid-cols-1 max-[700px]:[&>button]:w-full">
              {adminSettings?.apiKeyConfigured ? (
                <Button type="button" color="danger" loading={clearingApiKey} onClick={() => void clearApiKey()}>
                  {t('clearAiApiKey')}
                </Button>
              ) : null}
              <Button
                type="button"
                loading={testing}
                disabled={!apiUrl.trim() || !model.trim()}
                onClick={() => void testConnection()}
              >
                <Network className="size-4!" />
                {t('testAiConnection')}
              </Button>
              <Button variant="primary" compact type="submit" loading={saving} disabled={updatingEnabled}>
                <Save className="size-4!" />
                {t('saveSettings')}
              </Button>
            </div>
          </form>
        </SettingsCard>
      ) : null}
    </section>
  );
};

const formatStorageSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** exponent).toFixed(exponent > 1 ? 1 : 0)} ${units[exponent]}`;
};

const rateLimitToKibibytes = (bytesPerSecond: number): string =>
  bytesPerSecond === -1 ? '-1' : String(Math.round(bytesPerSecond / 1024));

const parseSettingInteger = (value: string): number | undefined => {
  const normalized = value.trim();
  if (!/^-?\d+$/.test(normalized)) {
    return undefined;
  }
  const parsed = Number(normalized);
  return Number.isSafeInteger(parsed) && parsed >= -1 ? parsed : undefined;
};

const kibibytesToRateLimit = (value: string): number | undefined => {
  const kibibytes = parseSettingInteger(value);
  if (kibibytes === undefined || kibibytes === -1) {
    return kibibytes;
  }
  const bytesPerSecond = kibibytes * 1024;
  return Number.isSafeInteger(bytesPerSecond) ? bytesPerSecond : undefined;
};

const commonRateLimits = [128, 256, 512, 1024, 2048, 5120, 10240, 20480, 51200] as const;

const formatRateLimitOption = (kibibytes: number): string =>
  kibibytes >= 1024 ? `${kibibytes / 1024} MiB/s` : `${kibibytes} KiB/s`;

const mediaTemplateContextGroups = (sites: MediaProviderTemplateContext['sites']) => [
  {
    name: 'bangumi',
    description: undefined,
    fields: [
      { path: 'bangumi.id', description: t('templateBangumiId') },
      { path: 'bangumi.name', description: t('templateBangumiName') },
      { path: 'bangumi.nameCn', description: t('templateBangumiNameCn') },
    ],
  },
  {
    name: 'episode',
    description: undefined,
    fields: [
      { path: 'episode.id', description: t('templateEpisodeId') },
      { path: 'episode.no', description: t('templateEpisodeNo') },
      { path: 'episode.noPadded', description: t('templateEpisodeNoPadded') },
      { path: 'episode.sort', description: t('templateEpisodeSort') },
    ],
  },
  {
    name: 'sites',
    description: (
      <>
        {t('templateSitesSource')}{' '}
        <a
          className="text-(--input-accent) underline-offset-2 hover:underline"
          href="https://github.com/bangumi-data/bangumi-data"
          target="_blank"
          rel="noreferrer"
        >
          bangumi-data
        </a>
        {' · '}
        {t('templateSitesDescription')}
      </>
    ),
    fields: sites.map((site) => ({
      path: `bangumi.sites.${site.name}`,
      description: site.title,
    })),
  },
];

const mediaTemplateVariable = (path: string): string => `{{ ${path} }}`;

const mediaProviderFaviconUrl = (urlTemplate: string): string | undefined => {
  try {
    const url = new URL(urlTemplate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return undefined;
    }
    return new URL('/favicon.ico', url.origin).toString();
  } catch {
    return undefined;
  }
};

const MediaProviderSiteIcon = ({ urlTemplate }: { urlTemplate: string }) => {
  const faviconUrl = mediaProviderFaviconUrl(urlTemplate);
  const [faviconFailed, setFaviconFailed] = useState(false);

  useEffect(() => setFaviconFailed(false), [faviconUrl]);

  return (
    <span
      className="grid size-9 place-items-center overflow-hidden rounded-lg border border-(--line) bg-(--surface) text-(--muted)"
      aria-hidden="true"
    >
      {faviconUrl && !faviconFailed ? (
        <img
          className="size-5 object-contain"
          src={faviconUrl}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFaviconFailed(true)}
        />
      ) : (
        <Globe2 className="size-4" />
      )}
    </span>
  );
};

const DownloadManagement = ({ visibleCards }: FilteredSettingsProps) => {
  const { api, locale, showToast } = useApp();
  const { settings, updateSettings, updateSettingsDebounced, cancelSettingsUpdate } = useSettings();
  const [storage, setStorage] = useState<StorageInfo>();
  const [tasks, setTasks] = useState<DownloadManagementTask[]>([]);
  const [savingTrackers, setSavingTrackers] = useState(false);
  const [downloadRateLimit, setDownloadRateLimit] = useState('0');
  const [uploadRateLimit, setUploadRateLimit] = useState('0');
  const [downloadRetentionHours, setDownloadRetentionHours] = useState('-1');
  const settingInputsInitializedRef = useRef(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [deletingTask, setDeletingTask] = useState(false);
  const [pendingDeleteTask, setPendingDeleteTask] = useState<DownloadManagementTask>();
  const [refreshingTasks, setRefreshingTasks] = useState(false);

  const loadStorage = useCallback(async () => {
    try {
      setStorage(await api<StorageInfo>('/api/storage'));
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    }
  }, [api, showToast]);

  useEffect(() => void loadStorage(), [loadStorage]);

  useEffect(() => {
    if (!settings || settingInputsInitializedRef.current) {
      return;
    }
    settingInputsInitializedRef.current = true;
    setDownloadRateLimit(rateLimitToKibibytes(settings.downloadRateLimit));
    setUploadRateLimit(rateLimitToKibibytes(settings.uploadRateLimit));
    setDownloadRetentionHours(String(settings.downloadRetentionHours));
  }, [settings]);

  const loadTasks = useCallback(async () => {
    setTasks(await api<DownloadManagementTask[]>('/api/downloads'));
  }, [api]);

  const refreshTasks = async (): Promise<void> => {
    setRefreshingTasks(true);
    try {
      await loadTasks();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setRefreshingTasks(false);
    }
  };

  useEffect(() => {
    void loadTasks().catch((reason) => {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    });
    const interval = setInterval(() => void loadTasks().catch(() => undefined), 2_000);
    return () => clearInterval(interval);
  }, [loadTasks, showToast]);

  const deleteTask = async (): Promise<void> => {
    if (!pendingDeleteTask) {
      return;
    }
    setDeletingTask(true);
    try {
      await api(`/api/downloads/${pendingDeleteTask.uuid}`, { method: 'DELETE' });
      setPendingDeleteTask(undefined);
      await Promise.all([loadTasks(), loadStorage()]);
      showToast(t('downloadDeleted'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setDeletingTask(false);
    }
  };

  const updateTrackers = async (autoUpdateTrackers: boolean): Promise<void> => {
    setSavingTrackers(true);
    try {
      await updateSettings({ autoUpdateTrackers });
    } catch {
      // useSettings reports update errors.
    } finally {
      setSavingTrackers(false);
    }
  };

  const changeRateLimit = (key: 'downloadRateLimit' | 'uploadRateLimit', value: string): void => {
    if (key === 'downloadRateLimit') {
      setDownloadRateLimit(value);
    } else {
      setUploadRateLimit(value);
    }
    const rateLimit = kibibytesToRateLimit(value);
    if (rateLimit === undefined) {
      cancelSettingsUpdate(key);
      return;
    }
    updateSettingsDebounced({ [key]: rateLimit });
  };

  const changeDownloadRetention = (value: string): void => {
    setDownloadRetentionHours(value);
    const hours = parseSettingInteger(value);
    if (hours === undefined) {
      cancelSettingsUpdate('downloadRetentionHours');
      return;
    }
    updateSettingsDebounced({ downloadRetentionHours: hours });
  };

  const clearCompleted = async (): Promise<void> => {
    setClearing(true);
    try {
      const result = await api<{ removed: number }>('/api/downloads/completed', { method: 'DELETE' });
      setClearDialogOpen(false);
      await loadStorage();
      showToast(`${t('completedDownloadsCleared')}: ${result.removed}`, 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setClearing(false);
    }
  };

  const diskTotal = Math.max(storage?.diskTotalBytes ?? 0, 1);
  const downloadsAngle = Math.min(((storage?.downloadsBytes ?? 0) / diskTotal) * 360, 360);
  const usedAngle = Math.min(((diskTotal - (storage?.diskFreeBytes ?? diskTotal)) / diskTotal) * 360, 360);
  const chartStyle = {
    '--downloads-angle': `${downloadsAngle}deg`,
    '--used-angle': `${Math.max(downloadsAngle, usedAngle)}deg`,
  } as CSSProperties;
  const rateLimitOptions = [
    { value: '-1', label: t('unlimitedRate') },
    { value: '0', label: t('stoppedRate') },
    ...commonRateLimits.map((rate) => ({ value: String(rate), label: formatRateLimitOption(rate) })),
  ];
  const downloadRetentionOptions = [
    { value: '-1', label: t('keepDownloadsForever') },
    ...[1, 3, 7, 14, 30, 90].map((days) => ({
      value: String(days * 24),
      label: t('keepDownloadsForDays').replace('{days}', String(days)),
    })),
  ];

  return (
    <div className="settings-panel grid gap-4">
      {isSettingsCardVisible(visibleCards, 'downloadTasks') ? (
        <SettingsCard
          className="download-tasks-card"
          icon={CloudDownload}
          title={t('downloadTasks')}
          description={t('downloadTasksDescription')}
          headingClassName="download-tasks-heading"
          headingAction={
            <Button iconOnly loading={refreshingTasks} aria-label={t('refresh')} onClick={() => void refreshTasks()}>
              <RefreshCw className="size-4!" />
            </Button>
          }
        >
          {tasks.length === 0 ? (
            <div className="download-tasks-empty">{t('noDownloadTasks')}</div>
          ) : (
            <div className="grid gap-2">
              {tasks.map((task) => (
                <div className="download-task-row" key={task.uuid}>
                  <div className="download-task-copy grid min-w-0 gap-1.5">
                    <strong title={task.name ?? task.uuid}>{task.name ?? task.uuid}</strong>
                    <small className="whitespace-nowrap">
                      {formatStorageSize(task.downloadedBytes)} / {formatStorageSize(task.totalBytes)}
                    </small>
                    {task.error ? <small className="download-task-error">{task.error}</small> : null}
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      iconOnly
                      color="danger"
                      aria-label={t('deleteDownload')}
                      title={t('deleteDownload')}
                      onClick={() => setPendingDeleteTask(task)}
                    >
                      <Trash2 className="size-4!" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SettingsCard>
      ) : null}

      {isSettingsCardVisible(visibleCards, 'storageSpace') ? (
        <SettingsCard
          className="download-storage-card"
          icon={HardDrive}
          title={t('storageSpace')}
          description={t('storageSpaceDescription')}
        >
          <div className="storage-overview">
            <div className="storage-pie" style={chartStyle} role="img" aria-label={t('storageSpace')}>
              <span>{formatStorageSize(storage?.downloadsBytes ?? 0)}</span>
            </div>
            <dl className="storage-legend">
              <div className="downloads">
                <dt>{t('downloadsFolder')}</dt>
                <dd>{storage ? formatStorageSize(storage.downloadsBytes) : '—'}</dd>
              </div>
              <div className="other">
                <dt>{t('otherDiskUsage')}</dt>
                <dd>
                  {storage
                    ? formatStorageSize(
                        Math.max(storage.diskTotalBytes - storage.diskFreeBytes - storage.downloadsBytes, 0),
                      )
                    : '—'}
                </dd>
              </div>
              <div className="free">
                <dt>{t('diskFree')}</dt>
                <dd>{storage ? formatStorageSize(storage.diskFreeBytes) : '—'}</dd>
              </div>
              <div>
                <dt>{t('diskCapacity')}</dt>
                <dd>{storage ? formatStorageSize(storage.diskTotalBytes) : '—'}</dd>
              </div>
            </dl>
          </div>
        </SettingsCard>
      ) : null}

      {isSettingsCardVisible(visibleCards, 'downloadRetention') ? (
        <SettingsCard icon={Clock3} title={t('downloadRetention')} description={t('downloadRetentionDescription')}>
          <label className={settingsFieldClassName}>
            <span>{t('downloadRetentionHours')}</span>
            <Select
              editable
              ariaLabel={t('downloadRetention')}
              options={downloadRetentionOptions}
              value={downloadRetentionHours}
              inputProps={{ type: 'number', min: -1, step: 1, required: true }}
              onChange={changeDownloadRetention}
            />
          </label>
        </SettingsCard>
      ) : null}

      {isSettingsCardVisible(visibleCards, 'transferRateLimits') ? (
        <SettingsCard icon={Gauge} title={t('transferRateLimits')} description={t('transferRateLimitsDescription')}>
          <div className="grid grid-cols-2 gap-4 max-[700px]:grid-cols-1">
            <label className={settingsFieldClassName}>
              <span>{t('downloadRateLimit')} (KiB/s)</span>
              <Select
                editable
                ariaLabel={t('downloadRateLimit')}
                options={rateLimitOptions}
                value={downloadRateLimit}
                inputProps={{ type: 'number', min: -1, step: 1, required: true }}
                onChange={(value) => changeRateLimit('downloadRateLimit', value)}
              />
            </label>
            <label className={settingsFieldClassName}>
              <span>{t('uploadRateLimit')} (KiB/s)</span>
              <Select
                editable
                ariaLabel={t('uploadRateLimit')}
                options={rateLimitOptions}
                value={uploadRateLimit}
                inputProps={{ type: 'number', min: -1, step: 1, required: true }}
                onChange={(value) => changeRateLimit('uploadRateLimit', value)}
              />
            </label>
          </div>
        </SettingsCard>
      ) : null}

      {isSettingsCardVisible(visibleCards, 'useAdditionalTrackers') ? (
        <SettingsCard
          className="appearance-card"
          icon={Network}
          title={t('useAdditionalTrackers')}
          description={t('useAdditionalTrackersDescription')}
          headingDetails={
            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
              <small className="tracker-cache-status">
                {t('trackerCache')}:{' '}
                {settings?.trackersUpdatedAt
                  ? `${settings.trackerCount} · ${new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(settings.trackersUpdatedAt))}`
                  : t('trackerCacheEmpty')}
              </small>
              {settings ? (
                <a className="tracker-source-link" href={settings.trackerSourceUrl} target="_blank" rel="noreferrer">
                  <span>{t('trackerSourceRepository')}</span>
                  <ExternalLink />
                </a>
              ) : null}
            </div>
          }
        >
          <Switch
            label={t('useAdditionalTrackers')}
            checked={settings?.autoUpdateTrackers ?? false}
            disabled={!settings || savingTrackers}
            onChange={(checked) => void updateTrackers(checked)}
          />
        </SettingsCard>
      ) : null}

      {isSettingsCardVisible(visibleCards, 'clearDownloads') ? (
        <SettingsCard
          className="settings-danger-card"
          icon={Trash2}
          title={t('clearDownloads')}
          description={t('clearDownloadsDescription')}
        >
          <Button color="danger" compact onClick={() => setClearDialogOpen(true)}>
            <Trash2 className="size-4!" />
            <span>{t('clearCompletedDownloads')}</span>
          </Button>
        </SettingsCard>
      ) : null}

      <Dialog
        open={Boolean(pendingDeleteTask)}
        title={t('confirmDeleteDownload')}
        description={
          <>
            <strong>{pendingDeleteTask?.name ?? pendingDeleteTask?.uuid}</strong>
            <p>{t('confirmDeleteDownloadDescription')}</p>
          </>
        }
        confirmText={t('deleteDownload')}
        confirmColor="danger"
        loading={deletingTask}
        onCancel={() => setPendingDeleteTask(undefined)}
        onConfirm={() => void deleteTask()}
      />

      <Dialog
        open={clearDialogOpen}
        title={t('confirmClearDownloads')}
        description={t('confirmClearDownloadsDescription')}
        confirmText={t('clearCompletedDownloads')}
        confirmColor="danger"
        loading={clearing}
        onCancel={() => setClearDialogOpen(false)}
        onConfirm={() => void clearCompleted()}
      />
    </div>
  );
};

const MediaProviderManagement = ({ visibleCards }: FilteredSettingsProps) => {
  const { api, showToast } = useApp();
  const [providers, setProviders] = useState<MediaProviderInfo[]>([]);
  const [templateContext, setTemplateContext] = useState<MediaProviderTemplateContext>({ sites: [] });
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string>();
  const [editingProvider, setEditingProvider] = useState<MediaProviderInfo>();
  const [editTemplate, setEditTemplate] = useState('');
  const [deletingProvider, setDeletingProvider] = useState<MediaProviderInfo>();
  const [templateReferenceOpen, setTemplateReferenceOpen] = useState(false);
  const [copiedTemplatePath, setCopiedTemplatePath] = useState<string>();
  const [refreshingProviders, setRefreshingProviders] = useState(false);

  const load = useCallback(async () => {
    const [nextProviders, nextTemplateContext] = await Promise.all([
      api<MediaProviderInfo[]>('/api/media-providers'),
      api<MediaProviderTemplateContext>('/api/media-providers/template-context'),
    ]);
    setProviders(nextProviders);
    setTemplateContext(nextTemplateContext);
  }, [api]);

  useEffect(() => {
    void load().catch((reason) => showToast(reason instanceof Error ? reason.message : String(reason), 'error'));
  }, [load, showToast]);

  const refreshProviders = async (): Promise<void> => {
    setRefreshingProviders(true);
    try {
      await load();
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setRefreshingProviders(false);
    }
  };

  const create = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    setCreating(true);
    try {
      await api<MediaProviderInfo>('/api/media-providers', {
        method: 'POST',
        body: JSON.stringify({
          id: data.get('id'),
          urlTemplate: data.get('urlTemplate'),
        }),
      });
      form.reset();
      await load();
      showToast(t('mediaProviderCreated'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setCreating(false);
    }
  };

  const update = async (provider: MediaProviderInfo, values: { urlTemplate?: string; enabled?: boolean }) => {
    setBusyId(provider.id);
    try {
      await api<MediaProviderInfo>(`/api/media-providers/${encodeURIComponent(provider.id)}`, {
        method: 'PATCH',
        body: JSON.stringify(values),
      });
      setEditingProvider(undefined);
      await load();
      if (values.urlTemplate !== undefined) {
        showToast(t('mediaProviderUpdated'), 'success');
      }
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setBusyId(undefined);
    }
  };

  const remove = async (): Promise<void> => {
    if (!deletingProvider) return;
    setBusyId(deletingProvider.id);
    try {
      await api(`/api/media-providers/${encodeURIComponent(deletingProvider.id)}`, { method: 'DELETE' });
      setDeletingProvider(undefined);
      await load();
      showToast(t('mediaProviderDeleted'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setBusyId(undefined);
    }
  };

  const copyTemplatePath = async (path: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(mediaTemplateVariable(path));
      setCopiedTemplatePath(path);
      setTimeout(() => setCopiedTemplatePath((current) => (current === path ? undefined : current)), 1_500);
    } catch {
      showToast(t('copyFailed'), 'error');
    }
  };

  return (
    <section className="settings-panel grid gap-4">
      {isSettingsCardVisible(visibleCards, 'rssMediaProviders') ? (
        <SettingsCard
          icon={Rss}
          title={t('rssMediaProviders')}
          description={t('mediaProvidersDescription')}
          headingClassName="grid-cols-[34px_minmax(0,1fr)_auto] items-start"
          headingAction={
            <div className="flex items-center gap-2">
              <Button
                compact
                aria-label={t('mediaProviderTemplateContext')}
                title={t('mediaProviderTemplateContext')}
                onClick={() => setTemplateReferenceOpen(true)}
              >
                <TableProperties className="size-4!" />
                <span className="max-[700px]:hidden">{t('mediaProviderTemplateContext')}</span>
              </Button>
              <Button
                iconOnly
                loading={refreshingProviders}
                aria-label={t('refresh')}
                title={t('refresh')}
                onClick={() => void refreshProviders()}
              >
                <RefreshCw className="size-4!" />
              </Button>
            </div>
          }
        >
          <form
            className="grid grid-cols-[minmax(150px,0.35fr)_minmax(0,1fr)_auto] items-end gap-3 max-[700px]:grid-cols-1"
            onSubmit={(event) => void create(event)}
          >
            <Input
              fieldClassName={settingsFieldClassName}
              label={t('mediaProviderId')}
              name="id"
              placeholder={t('mediaProviderIdPlaceholder')}
              pattern="[a-z0-9][a-z0-9._-]{0,63}"
              required
            />
            <Input
              fieldClassName={settingsFieldClassName}
              label={t('mediaProviderUrlTemplate')}
              name="urlTemplate"
              placeholder={t('mediaProviderUrlTemplatePlaceholder')}
              required
            />
            <Button className="max-[700px]:w-full" variant="primary" compact type="submit" disabled={creating}>
              <Plus className="size-4!" />
              {t('addMediaProvider')}
            </Button>
          </form>
          <div className="mt-6 border-t border-(--line) pt-6">
            {providers.length === 0 ? (
              <div className="download-tasks-empty">{t('noMediaProviders')}</div>
            ) : (
              <div className="grid gap-2">
                {providers.map((provider) => (
                  <div
                    className="grid min-w-0 grid-cols-[36px_minmax(0,1fr)_auto] items-center gap-3 rounded-xl border border-(--line) bg-(--surface-2) px-4 py-3 max-[700px]:grid-cols-[36px_minmax(0,1fr)] max-[700px]:px-3"
                    key={provider.id}
                  >
                    <MediaProviderSiteIcon urlTemplate={provider.urlTemplate} />
                    <div className="grid min-w-0 gap-1">
                      <strong className="text-xs">{provider.id}</strong>
                      <small className="truncate font-mono text-[9px] text-(--muted)" title={provider.urlTemplate}>
                        {provider.urlTemplate}
                      </small>
                    </div>
                    <div className="flex items-center gap-2 max-[700px]:col-span-2 max-[700px]:w-full max-[700px]:justify-end">
                      <Switch
                        className="h-9 justify-center"
                        label={t('mediaProviderEnabled')}
                        checked={provider.enabled}
                        disabled={Boolean(busyId)}
                        onChange={(enabled) => void update(provider, { enabled })}
                      />
                      <Button
                        iconOnly
                        aria-label={t('editMediaProvider')}
                        title={t('editMediaProvider')}
                        disabled={Boolean(busyId)}
                        onClick={() => {
                          setEditingProvider(provider);
                          setEditTemplate(provider.urlTemplate);
                        }}
                      >
                        <Pencil className="size-4!" />
                      </Button>
                      <Button
                        iconOnly
                        color="danger"
                        aria-label={t('deleteMediaProvider')}
                        title={t('deleteMediaProvider')}
                        disabled={Boolean(busyId)}
                        onClick={() => setDeletingProvider(provider)}
                      >
                        <Trash2 className="size-4!" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SettingsCard>
      ) : null}

      <Dialog
        open={templateReferenceOpen}
        title={t('mediaProviderTemplateContext')}
        description={
          <div>
            <p className="m-0">{t('mediaProviderTemplateContextDescription')}</p>
            {mediaTemplateContextGroups(templateContext.sites).map((group) => (
              <section className="mt-4" key={group.name}>
                <h3 className="m-0 mb-2 font-mono text-xs text-(--text)">{group.name}</h3>
                {group.description ? <p className="mt-0 mb-2 text-[10px] text-(--muted)">{group.description}</p> : null}
                <div className="grid grid-cols-2 gap-2 max-[700px]:grid-cols-1">
                  {group.fields.map((field) => (
                    <Button
                      variant="plain"
                      className="flex min-w-0 cursor-pointer items-center justify-between gap-2 rounded-lg border border-(--line) bg-(--surface-2) px-3 py-2 text-left hover:border-[color-mix(in_srgb,var(--input-accent)_42%,var(--line))] hover:text-(--input-accent) focus-visible:border-[color-mix(in_srgb,var(--input-accent)_42%,var(--line))] focus-visible:text-(--input-accent) focus-visible:outline-0"
                      title={t('copyTemplateVariable')}
                      aria-label={`${t('copyTemplateVariable')}: ${mediaTemplateVariable(field.path)}`}
                      key={field.path}
                      onClick={() => void copyTemplatePath(field.path)}
                    >
                      <span className="grid min-w-0 gap-0.5">
                        <code className="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[10px]">
                          {mediaTemplateVariable(field.path)}
                        </code>
                        <small className="overflow-hidden text-ellipsis whitespace-nowrap text-[9px] text-(--muted)">
                          {field.description}
                        </small>
                      </span>
                      {copiedTemplatePath === field.path ? (
                        <Check className="size-3.5 flex-none" />
                      ) : (
                        <Copy className="size-3.5 flex-none" />
                      )}
                    </Button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        }
        hideConfirm
        panelClassName="max-w-192!"
        onCancel={() => setTemplateReferenceOpen(false)}
        onConfirm={() => setTemplateReferenceOpen(false)}
      />

      <Dialog
        open={Boolean(editingProvider) && !templateReferenceOpen}
        title={`${t('editMediaProvider')}: ${editingProvider?.id ?? ''}`}
        description={
          <Input
            fieldClassName={`${settingsFieldClassName} mt-3`}
            label={t('mediaProviderUrlTemplate')}
            value={editTemplate}
            onChange={(event) => setEditTemplate(event.currentTarget.value)}
          />
        }
        footerAction={
          <Button compact onClick={() => setTemplateReferenceOpen(true)}>
            <TableProperties className="size-4!" />
            <span>{t('mediaProviderTemplateContext')}</span>
          </Button>
        }
        confirmText={t('saveSettings')}
        confirmDisabled={!editTemplate.trim()}
        loading={Boolean(editingProvider && busyId === editingProvider.id)}
        panelClassName="max-w-140!"
        onCancel={() => setEditingProvider(undefined)}
        onConfirm={() => editingProvider && void update(editingProvider, { urlTemplate: editTemplate.trim() })}
      />

      <Dialog
        open={Boolean(deletingProvider)}
        title={t('confirmDeleteMediaProvider')}
        description={
          <>
            <strong>{deletingProvider?.id}</strong>
            <p>{t('confirmDeleteMediaProviderDescription')}</p>
          </>
        }
        confirmText={t('deleteMediaProvider')}
        confirmColor="danger"
        loading={Boolean(deletingProvider && busyId === deletingProvider.id)}
        onCancel={() => setDeletingProvider(undefined)}
        onConfirm={() => void remove()}
      />
    </section>
  );
};

const UserManagement = ({ currentUser, visibleCards }: { currentUser: CurrentUser } & FilteredSettingsProps) => {
  const { api, showToast } = useApp();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [role, setRole] = useState('user');
  const [pendingUser, setPendingUser] = useState<UserInfo>();
  const [deleting, setDeleting] = useState(false);
  const load = useCallback(async () => setUsers(await api<UserInfo[]>('/api/users')), [api]);
  useEffect(() => void load(), [load]);
  const create = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    try {
      await api('/api/users', {
        method: 'POST',
        body: JSON.stringify({ name: data.get('name'), password: data.get('password'), role: data.get('role') }),
      });
      form.reset();
      setRole('user');
      await load();
      showToast(t('userCreated'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    }
  };
  const remove = async (): Promise<void> => {
    if (!pendingUser) {
      return;
    }
    setDeleting(true);
    try {
      await api(`/api/users/${pendingUser.id}`, { method: 'DELETE' });
      await load();
      setPendingUser(undefined);
      showToast(t('userDeleted'), 'success');
    } catch (reason) {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    } finally {
      setDeleting(false);
    }
  };
  if (!isSettingsCardVisible(visibleCards, 'users')) {
    return null;
  }
  return (
    <section className="settings-panel">
      <SettingsCard icon={Users} title={t('users')} description={t('usersDescription')}>
        <form
          className="mb-3.5 grid grid-cols-[1fr_1fr_110px_auto] gap-2.25 max-[700px]:grid-cols-1"
          onSubmit={(event) => void create(event)}
        >
          <Input name="name" placeholder={t('username')} required />
          <Input name="password" type="password" minLength={6} placeholder={t('password')} required />
          <Select
            className="user-role-select"
            name="role"
            value={role}
            onChange={setRole}
            options={[
              { label: 'User', value: 'user' },
              { label: 'Admin', value: 'admin' },
            ]}
          />
          <Button variant="primary" compact type="submit">
            <UserPlus />
            <span>{t('createUser')}</span>
          </Button>
        </form>
        <Divider />
        <div className="grid">
          {users.map((item) => (
            <Fragment key={item.id}>
              <div className="grid grid-cols-[34px_1fr_auto] items-center gap-2.75 px-1 py-3">
                <div className="avatar size-8.5 text-[11px]">{item.name.slice(0, 1).toUpperCase()}</div>
                <div>
                  <strong className="block text-xs">{item.name}</strong>
                  <span className="mt-0.5 block text-[9px] text-(--muted) uppercase">{item.role}</span>
                </div>
                <Button
                  iconOnly
                  color="danger"
                  disabled={item.id === currentUser.id}
                  onClick={() => setPendingUser(item)}
                >
                  <Trash2 className="size-5!" />
                </Button>
              </div>
              <Divider />
            </Fragment>
          ))}
        </div>
      </SettingsCard>
      <Dialog
        open={Boolean(pendingUser)}
        title={`${t('deleteUser')}: ${pendingUser?.name ?? ''}`}
        description={t('deleteUserDescription')}
        confirmText={t('deleteUser')}
        confirmColor="danger"
        loading={deleting}
        onCancel={() => setPendingUser(undefined)}
        onConfirm={() => void remove()}
      />
    </section>
  );
};
